import apiClient from "./axiosConfig";

// Get batches list with search
export const getBatchesList = async (params = {}) => {
  try {
    const { search = '', page = 1, page_size = 10 } = params;
    const queryParams = new URLSearchParams();
    
    if (search) queryParams.append('search', search);
    if (page) queryParams.append('page', page);
    if (page_size) queryParams.append('page_size', page_size);
    
    const { store } = await import("../store");
    const state = store.getState();
    if (state.domain.isGodownURL) {
      queryParams.append('is_godown', 'true');
    }
    
    const response = await apiClient.get(`/stock-management/batches/?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching batches:', error);
    throw error;
  }
};

// Get batch detail
export const getBatchDetail = async (batchId) => {
  try {
    const { store } = await import("../store");
    const state = store.getState();
    const queryParams = state.domain.isGodownURL ? '?is_godown=true' : '';
    const response = await apiClient.get(`/stock-management/batches/${batchId}/${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching batch detail:', error);
    throw error;
  }
};

// Create new batch
export const createBatch = async (batchData) => {
  try {
    const payload = {
      invoice_number: batchData.invoiceNumber,
      amount: parseFloat(batchData.amount),
      quantity: parseInt(batchData.totalQuantity),
      vendor: parseInt(batchData.supplierId),
      description: batchData.description || ""
    };
    
    const { store } = await import("../store");
    const state = store.getState();
    const queryParams = state.domain.isGodownURL ? '?is_godown=true' : '';
    const response = await apiClient.post(`/stock-management/batches/${queryParams}`, payload);
    return response.data;
  } catch (error) {
    console.error('Error creating batch:', error);
    throw error;
  }
};

// Update batch
export const updateBatch = async (batchId, batchData) => {
  try {
    const payload = {
      invoice_number: batchData.invoiceNumber,
      amount: parseFloat(batchData.amount),
      quantity: parseInt(batchData.totalQuantity),
      vendor: parseInt(batchData.supplierId),
      description: batchData.description || ""
    };
    
    const { store } = await import("../store");
    const state = store.getState();
    const queryParams = state.domain.isGodownURL ? '?is_godown=true' : '';
    const response = await apiClient.patch(`/stock-management/batches/${batchId}/${queryParams}`, payload);
    return response.data;
  } catch (error) {
    console.error('Error updating batch:', error);
    throw error;
  }
};

// Delete batch
export const deleteBatch = async (batchId) => {
  try {
    const { store } = await import("../store");
    const state = store.getState();
    const queryParams = state.domain.isGodownURL ? '?is_godown=true' : '';
    const response = await apiClient.delete(`/stock-management/batches/${batchId}/${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting batch:', error);
    throw error;
  }
};

// Get batch count and statistics
export const getBatchCount = async () => {
  try {
    const { store } = await import("../store");
    const state = store.getState();
    const queryParams = state.domain.isGodownURL ? '?is_godown=true' : '';
    const response = await apiClient.get(`/stock-management/batches/count/${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching batch count:', error);
    throw error;
  }
};

// Get batch purchases list with search and pagination
export const getBatchPurchasesList = async (params = {}) => {
  try {
    const { search = '', page = 1, page_size = 10, batch_id } = params;
    const queryParams = new URLSearchParams();
    
    if (search) queryParams.append('search', search);
    if (page) queryParams.append('page', page);
    if (page_size) queryParams.append('page_size', page_size);
    if (batch_id) queryParams.append('batch_id', batch_id);
    queryParams.append('is_godown', 'true');
    
    const response = await apiClient.get(`/stock-management/purchases/?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching batch purchases:', error);
    throw error;
  }
};

// Create batch purchase
export const createBatchPurchase = async (purchaseData) => {
  try {
    const response = await apiClient.post('/stock-management/purchases/?is_godown=true', purchaseData);
    return response.data;
  } catch (error) {
    console.error('Error creating batch purchase:', error);
    throw error;
  }
};

// Get batch purchase by tag
export const getBatchPurchaseByTag = async (params) => {
  try {
    const { unique_identifier, batch_id } = params;
    const response = await apiClient.get(
      `/stock-management/purchases/by-tag/?unique_identifier=${unique_identifier}&batch_id=${batch_id}&is_godown=true`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching batch purchase by tag:', error);
    throw error;
  }
};

// Create batch purchase by tag
export const createBatchPurchaseByTag = async (purchaseData) => {
  try {
    const response = await apiClient.post('/stock-management/purchases/by-tag/?is_godown=true', purchaseData);
    return response.data;
  } catch (error) {
    console.error('Error creating batch purchase by tag:', error);
    throw error;
  }
};

// Update batch purchase by tag
export const updateBatchPurchaseByTag = async (purchaseData) => {
  try {
    const response = await apiClient.patch('/stock-management/purchases/by-tag/?is_godown=true', purchaseData);
    return response.data;
  } catch (error) {
    console.error('Error updating batch purchase by tag:', error);
    throw error;
  }
};

// Update batch purchase
export const updateBatchPurchase = async (purchaseId, purchaseData) => {
  try {
    const response = await apiClient.patch(`/stock-management/purchases/${purchaseId}/?is_godown=true`, purchaseData);
    return response.data;
  } catch (error) {
    console.error('Error updating batch purchase:', error);
    throw error;
  }
};

// Delete batch purchase
export const deleteBatchPurchase = async (purchaseId) => {
  try {
    const response = await apiClient.delete(`/stock-management/purchases/${purchaseId}/?is_godown=true`);
    return response.data;
  } catch (error) {
    console.error('Error deleting batch purchase:', error);
    throw error;
  }
};

// Publish batch
export const publishBatch = async (batchId) => {
  try {
    const response = await apiClient.post('/stock-management/batches/publish/', {
      batch_id: batchId
    });
    return response.data;
  } catch (error) {
    console.error('Error publishing batch:', error);
    throw error;
  }
};